import { formatarDataHora, formatarDiaCurto } from '@common/i18n/formatosData';
import { Badge } from '@ntconsult/aluma-react';
import type { TFunction } from 'i18next';
import type { ReactNode } from 'react';
import { createColumnsHook } from '../hooks/createColumnsHook';
import type { ItemPainel, OrdenarPainel, StatusGravacao } from '../types/Painel';
import { ConviteBadge } from './ConviteBadge';
import { GravacaoBadge } from './GravacaoBadge';

/** Ordem de exibição; "seleção" e "Ações" são fixas e ficam fora. */
export const COLUNAS = [
  'matricula',
  'nome',
  'email',
  'resposta',
  'brinde',
  'camiseta',
  'endereco',
  'convite',
  'idioma',
  'acessos',
  'ultimoAcesso',
  'atualizadaEm',
  'origem',
  'por',
] as const;
export type Coluna = (typeof COLUNAS)[number];

/** Mesmos valores do formulário e da API (os do filtro por Camiseta). */
export const TAMANHOS = [
  'PP (XS)',
  'P (S)',
  'M (M)',
  'G (L)',
  'GG (XL)',
  'XG (XL1)',
  '2XG (XL2)',
  '3XG (XL3)',
] as const;

/** Só existem na aba Excluídos. */
export const SO_EXCLUIDOS: readonly Coluna[] = ['por'];

export const COLUNAS_PADRAO: readonly Coluna[] = [
  'matricula',
  'nome',
  'email',
  'resposta',
  'brinde',
  'convite',
  'ultimoAcesso',
  ...SO_EXCLUIDOS,
];

/** Colunas que a API ordena (spec 13 R14). */
export const ORDENAVEL: Partial<Record<Coluna, OrdenarPainel>> = {
  matricula: 'matricula',
  nome: 'nome',
  email: 'email',
  resposta: 'respondidaEm',
  acessos: 'acessos',
  ultimoAcesso: 'ultimoAcessoEm',
  atualizadaEm: 'atualizadaEm',
};

/** Chave do rótulo no namespace `participantes`. */
export const ROTULO: Record<Coluna, string> = {
  matricula: 'painel.colunas.matricula',
  nome: 'painel.colunas.nome',
  email: 'colunas.email',
  resposta: 'painel.colunas.resposta',
  brinde: 'painel.colunas.brinde',
  camiseta: 'painel.colunas.camiseta',
  endereco: 'painel.colunas.endereco',
  convite: 'painel.colunas.convite',
  idioma: 'colunas.idioma',
  acessos: 'colunas.acessos',
  ultimoAcesso: 'colunas.ultimoAcesso',
  atualizadaEm: 'painel.colunas.atualizadaEm',
  origem: 'colunas.origem',
  por: 'colunas.por',
};

/** Preferência de visualização, não filtro: fica no `localStorage`, não na URL. */
export const useColunasParticipantes = createColumnsHook<Coluna>({
  columns: COLUNAS,
  defaultColumns: COLUNAS_PADRAO,
  storageKey: 'my-update:participantes:colunas',
  version: 1,
});

const TOM_GRAVACAO: Record<StatusGravacao, 'warning' | 'success' | 'danger' | 'neutral'> = {
  pendente: 'warning',
  sucesso: 'success',
  falha: 'danger',
  cancelada: 'neutral',
};

/** "Nome (matrícula)" para `aria-label` e confirmações; sem matrícula, só o nome (spec 32 R7). */
export const identificacao = (p: { nomeExibicao: string; matricula: number | null }) =>
  p.matricula == null ? p.nomeExibicao : `${p.nomeExibicao} (${p.matricula})`;

const dataHora = (iso: string | null | undefined) => (iso ? formatarDataHora(iso) : '—');

/** "há 2 minutos" para acessos nas últimas 24 h; senão nada (a data já está na célula). */
function haQuanto(iso: string, idioma: string) {
  const min = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (min < 0 || min >= 1440) return null;
  const rtf = new Intl.RelativeTimeFormat(idioma, { numeric: 'auto' });
  return min < 60 ? rtf.format(-min, 'minute') : rtf.format(-Math.round(min / 60), 'hour');
}

export type ContextoCelula = {
  t: TFunction<readonly ['participantes', 'common', 'convites']>;
  idioma: string;
  compacta: boolean;
};

/** Conteúdo da célula de cada coluna. Endereço: só o selo, nunca o texto (spec 07 R6). */
export function celula(
  coluna: Coluna,
  p: ItemPainel,
  { t, idioma, compacta }: ContextoCelula,
): ReactNode {
  const respostaBadge = (
    <Badge tone={p.respondeu ? 'success' : 'neutral'}>
      {t(p.respondeu ? 'participantes:painel.respondeu' : 'participantes:painel.naoRespondeu')}
    </Badge>
  );
  switch (coluna) {
    case 'matricula':
      return p.matricula ?? '—';
    case 'nome': {
      // Duas linhas: o nome de exibição e, embaixo, o nome completo (se difere) e a matrícula,
      // que distingue homônimos (spec 32 R7). O botão não tem `onClick`: o clique sobe à linha (R2).
      const completo = p.nomes.nome !== p.nomeExibicao ? p.nomes.nome : null;
      const segunda = [completo, p.matricula].filter((v) => v != null).join(' · ');
      return (
        <div className="max-w-xs">
          <div className="flex items-center gap-2">
            <button
              type="button"
              aria-label={t('participantes:linha.abrir', { nome: identificacao(p) })}
              className="min-w-0 truncate rounded-sm text-left font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus"
            >
              {p.nomeExibicao}
            </button>
            {compacta && <span className="shrink-0">{respostaBadge}</span>}
          </div>
          {segunda && <div className="truncate text-xs text-foreground-muted">{segunda}</div>}
        </div>
      );
    }
    case 'email': {
      if (!p.email) return '—';
      // Quebra só depois do "@", nunca no meio de uma parte: o endereço fica inteiro na tela.
      const arroba = p.email.lastIndexOf('@') + 1;
      return (
        <span>
          <span className="whitespace-nowrap">{p.email.slice(0, arroba)}</span>
          <wbr />
          <span className="whitespace-nowrap">{p.email.slice(arroba)}</span>
        </span>
      );
    }
    case 'resposta':
      return compacta ? null : respostaBadge;
    case 'brinde':
      return p.desejaBrinde === null ? '—' : t(p.desejaBrinde ? 'common:yes' : 'common:no');
    case 'camiseta':
      return (
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="whitespace-nowrap">{p.camiseta.tamanhoFinal ?? '—'}</span>
          {p.camiseta.gravacao && <GravacaoBadge status={p.camiseta.gravacao} />}
        </div>
      );
    case 'endereco':
      return (
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="whitespace-nowrap">
            {p.endereco.entrega ? t(`participantes:painel.entrega.${p.endereco.entrega}`) : '—'}
          </span>
          {p.endereco.oficialSolicitado && (
            <Badge tone={TOM_GRAVACAO[p.endereco.gravacaoOficial ?? 'cancelada']}>
              {t('participantes:painel.oficial', {
                situacao: p.endereco.gravacaoOficial
                  ? t(`participantes:painel.gravacao.${p.endereco.gravacaoOficial}`)
                  : '—',
              })}
            </Badge>
          )}
        </div>
      );
    case 'convite':
      return (
        <div className="flex items-center gap-2">
          <ConviteBadge
            status={p.convite.status}
            resultado={p.convite.resultado}
            respondeu={p.respondeu}
          />
          {p.convite.atualizadoEm && (
            <span className="text-xs text-foreground-muted">
              {formatarDiaCurto(p.convite.atualizadoEm)}
            </span>
          )}
        </div>
      );
    case 'idioma':
      return t(`participantes:idiomas.${p.idioma}`);
    case 'acessos':
      return <span className={p.acessos ? undefined : 'text-foreground-muted'}>{p.acessos}</span>;
    case 'ultimoAcesso': {
      if (!p.ultimoAcessoEm)
        return <span className="text-foreground-muted">{t('participantes:acessos.nunca')}</span>;
      const ha = haQuanto(p.ultimoAcessoEm, idioma);
      return (
        <>
          {dataHora(p.ultimoAcessoEm)}
          {ha && <div className="text-xs text-foreground-muted">{ha}</div>}
        </>
      );
    }
    case 'atualizadaEm':
      return dataHora(p.atualizadaEm);
    case 'origem':
      return t(`participantes:origem.${p.incluidoOrigem}`);
    case 'por':
      return p.exclusao?.por || '—';
  }
}
